import { useEffect, useState } from "react";
import "./App.css";
import { authedFetch, authedPost, clearSiteToken } from "./api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wedding registry links. Blank values automatically hide the corresponding card.
const REGISTRY_LINKS = {
  honeyfund: "https://www.honeyfund.com/site/lariz-norman-11-07-2026",
};

function App() {
  const [scrolled, setScrolled] = useState(false);
  // RSVP section is hidden until a guest clicks "Confirm My Attendance" on the
  // pre-RSVP banner or the "Find My RSVP" nav link.
  const [rsvpVisible, setRsvpVisible] = useState(false);

  // RSVP Multi-step flow state (now only 2 steps: search + confirm/comment)
  const [rsvpStep, setRsvpStep] = useState('search'); // search | confirm | thankyou
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guestList, setGuestList] = useState([]); // list of {name, attending, attending_ceremony, attending_reception}
  const [contactInfo, setContactInfo] = useState({
    email: '',
    address: '',
    dietary_notes: '',
    song_request: '',
    message: '', // Questions or Comments box on step 2
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Auto-scroll guests to the "Confirm My Attendance" banner shortly
    // after landing, so anyone arriving from the QR code goes straight
    // to the action they need to take.
    window.scrollTo(0, 0);
    const scrollTimer = setTimeout(() => {
      const banner = document.querySelector('.confirm-banner');
      if (banner) banner.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 600);

    // Handle scroll for navigation
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(scrollTimer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleSearch = async (e, queryOverride) => {
    if (e) e.preventDefault();
    const query = (queryOverride !== undefined ? queryOverride : searchQuery).trim();
    // Require at least 2 tokens (first + last name) before searching
    const tokens = query.split(/\s+/).filter(t => t.length >= 2);
    if (tokens.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await authedPost('site', '/api/rsvp/search', { query });
      if (res.status === 401) {
        // Site token expired; force full reload → SiteGate will re-prompt.
        window.location.reload();
        return;
      }
      const data = await res.json();
      setSearchResults(data.results || []);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const selectGuest = async (guest) => {
    setSelectedGuest(guest);
    // Check if guest has an existing RSVP
    try {
      const res = await authedFetch('site', `/api/rsvp/check/${guest.id}`);
      if (res.status === 401) { window.location.reload(); return; }
      const data = await res.json();
      if (data.exists && data.rsvp) {
        // Merge saved guests with current master party (handles +1 added after submission)
        const masterParty = buildPartyMembers(guest);
        setGuestList(reconcileGuestList(data.rsvp.guests, masterParty));
        setContactInfo({
          email: data.rsvp.email || '',
          address: data.rsvp.address || '',
          dietary_notes: data.rsvp.dietary_notes || '',
          song_request: data.rsvp.song_request || '',
          message: data.rsvp.message || '',
        });
        setIsUpdating(true);
        setRsvpStep('confirm');
        return;
      }
    } catch (err) {
      console.error('Check existing RSVP failed:', err);
    }

    // No existing RSVP - build fresh
    setIsUpdating(false);
    setGuestList(buildPartyMembers(guest));
    setRsvpStep('confirm');
  };

  // Build the canonical party member list from the master guest record
  const buildPartyMembers = (guest) => {
    const base = { attending: true, attending_ceremony: true, attending_cocktail: true, attending_reception: true };
    const people = [{ name: guest.primary_guest, ...base }];
    if (guest.plus_one && guest.plus_one !== 'Guest' && guest.plus_one !== 'Plus One') {
      // Named plus-one from the guest list. Display, don't edit
      people.push({ name: guest.plus_one, ...base });
    } else if (guest.plus_one === 'Plus One' || guest.plus_one === 'Guest') {
      // Unnamed +1 placeholder. User must type in a name
      people.push({ name: '', ...base, isPlusOne: true });
    }
    if (guest.kids) {
      guest.kids.split(',').map(k => k.trim()).filter(Boolean).forEach(kidName => {
        people.push({ name: kidName, ...base });
      });
    }
    return people;
  };

  // Reconcile a saved RSVP guest list with the current master party.
  // Adds new party members (e.g. +1 added later) and preserves saved attending status.
  const reconcileGuestList = (savedGuests, masterParty) => {
    const saved = savedGuests || [];
    const result = [];
    const seen = new Set();
    // Master name lookup (excluding empty plus-one placeholders)
    const masterByName = {};
    for (const m of masterParty) {
      if (m.name) masterByName[m.name.toLowerCase()] = m;
    }
    // Preserve saved rows (attending + typed plus-one names)
    for (const g of saved) {
      if (!g || !g.name) continue;
      const key = g.name.toLowerCase();
      // Match against master by name; if not found, keep it (probably a previously-typed plus-one)
      if (masterByName[key]) {
        result.push(g);
        seen.add(key);
      } else {
        // Preserve any typed plus-one name from before
        result.push({ ...g, isPlusOne: true });
      }
    }
    // Append any master entries not yet in the list
    for (const m of masterParty) {
      const key = (m.name || '').toLowerCase();
      // For plus-one placeholders, only add if none of the saved rows is a plus-one
      if (m.isPlusOne) {
        const alreadyHasPlusOne = result.some(r => r.isPlusOne || !masterByName[(r.name || '').toLowerCase()]);
        if (!alreadyHasPlusOne) {
          result.push({ ...m });
        }
        continue;
      }
      if (key && !seen.has(key)) {
        result.push({ ...m, attending: true, attending_ceremony: true, attending_cocktail: true, attending_reception: true });
      }
    }
    // Ensure every row has all service flags (back-fill legacy saves)
    return result.map(r => {
      const hasCer = typeof r.attending_ceremony === 'boolean';
      const hasCoc = typeof r.attending_cocktail === 'boolean';
      const hasRec = typeof r.attending_reception === 'boolean';
      if (hasCer && hasCoc && hasRec) return r;
      const legacy = r.attending === false ? false : true;
      return {
        ...r,
        attending_ceremony: hasCer ? r.attending_ceremony : legacy,
        // Missing cocktail flag mirrors reception (both post-ceremony)
        attending_cocktail: hasCoc ? r.attending_cocktail : (hasRec ? r.attending_reception : legacy),
        attending_reception: hasRec ? r.attending_reception : legacy,
      };
    });
  };

  // Update one service flag for a single party member. Also keeps the aggregate
  // `attending` field in sync (true if attending ANY service).
  const updateGuestService = (idx, field, value) => {
    const updated = [...guestList];
    updated[idx] = { ...updated[idx], [field]: value };
    updated[idx].attending =
      !!updated[idx].attending_ceremony ||
      !!updated[idx].attending_cocktail ||
      !!updated[idx].attending_reception;
    setGuestList(updated);
  };

  const updateGuestName = (idx, name) => {
    const updated = [...guestList];
    updated[idx].name = name;
    setGuestList(updated);
  };

  const submitRsvp = async (e) => {
    e.preventDefault();
    setSubmitError('');

    // If a placeholder plus-one is marked attending, require a name.
    const missingName = guestList.some(
      (p) => p.isPlusOne === true && p.attending === true && !(p.name || '').trim()
    );
    if (missingName) {
      setSubmitError("Please enter your additional guest's name to continue.");
      return;
    }

    setSubmitting(true);
    try {
      // Trim placeholder names so we never persist stray whitespace.
      const cleanedGuests = guestList.map((p) =>
        p.isPlusOne ? { ...p, name: (p.name || '').trim() } : p
      );
      const payload = {
        guest_id: selectedGuest.id,
        primary_guest: selectedGuest.primary_guest,
        family: selectedGuest.family,
        guests: cleanedGuests,
        email: (contactInfo.email || '').trim() || 'no-email@placeholder.com',
        address: contactInfo.address,
        dietary_notes: contactInfo.dietary_notes,
        song_request: contactInfo.song_request,
        message: contactInfo.message,
      };
      const res = await authedPost('site', '/api/rsvp/submit', payload);
      if (res.status === 401) { window.location.reload(); return; }
      if (!res.ok) {
        // Surface backend validation messages (e.g. missing plus-one name)
        let detail = '';
        try { detail = (await res.json())?.detail || ''; } catch (_) {}
        throw new Error(detail || 'Submission failed');
      }
      setRsvpStep('thankyou');
    } catch (err) {
      setSubmitError(err?.message && err.message !== 'Submission failed'
        ? err.message
        : 'Something went wrong. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const resetRsvp = () => {
    setRsvpStep('search');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedGuest(null);
    setGuestList([]);
    setContactInfo({ email: '', address: '', dietary_notes: '', song_request: '', message: '' });
    setSubmitError('');
    setIsUpdating(false);
  };

  const scrollToSection = (id) => {
    // The RSVP section is hidden until the user actively wants to confirm.
    // Any navigation to it (nav link OR the pre-RSVP CTA button) reveals it
    // first, then scrolls after the DOM has rendered.
    if (id === 'rsvp') {
      setRsvpVisible(true);
      requestAnimationFrame(() => {
        setTimeout(() => {
          const el = document.getElementById('rsvp');
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 50);
      });
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="App">
      {/* Cinematic Intro with "We're Getting Married" Image */}
      {/* Main Website Content */}
      <div className="main-content">

      {/* Navigation with Monogram */}
      <nav className={scrolled ? 'scrolled' : ''}>
        <a href="#home" className="nav-logo">
          <img src="/monogram-std.png" alt="N&S Monogram" className="nav-monogram" />
        </a>
        <ul className="nav-links">
          <li><a onClick={() => scrollToSection('story')}>Our Story</a></li>
          <li><a onClick={() => scrollToSection('venue')}>Venue</a></li>
          <li><a onClick={() => scrollToSection('details')}>Details</a></li>
          <li><a onClick={() => scrollToSection('schedule')}>Schedule</a></li>
          <li><a onClick={() => scrollToSection('registry')} data-testid="nav-registry">Registry</a></li>
          <li><a onClick={() => scrollToSection('rsvp')} data-testid="nav-rsvp">Find My RSVP</a></li>
        </ul>
      </nav>

      {/* Hero Section */}
      <div className="hero" id="home">
        <div className="hero-img">
          <div className="hero-img-main"></div>
          <div className="hero-img-overlay"></div>
        </div>
        <div className="hero-content">
          <h1 className="hero-names">
            Nathan <em className="hero-amp">&</em> Sandra
          </h1>
          <div className="hero-tag">Save the Date</div>
          <div className="hero-date">November 7, 2026</div>
        </div>
      </div>

      {/* Confirmation Banner: prominent hero card right after intro */}
      <section className="confirm-banner" data-testid="confirm-banner">
        <div className="confirm-card">
          <div className="confirm-card-glow" aria-hidden="true" />
          <div className="confirm-card-inner">
            <img src="/monogram-std.png" alt="" aria-hidden="true" className="confirm-monogram" />
            <p className="confirm-preheading">We received your pre-RSVP.</p>
            <div className="confirm-eyebrow">Formally Invited</div>
            <div className="confirm-ornament" aria-hidden="true">
              <span className="confirm-orn-line" />
              <span className="confirm-orn-diamond">✦</span>
              <span className="confirm-orn-line" />
            </div>
            <p className="confirm-body">
              It means so much to have you with us. We know plans can change, so please confirm you can still join us and that your guest details are correct. We look forward to seeing you on our big day.
            </p>
            <p className="confirm-signature"><em>With love,<br />Nathan &amp; Sandra</em></p>
            <button
              type="button"
              className="confirm-cta"
              onClick={() => scrollToSection('rsvp')}
              data-testid="confirm-cta"
            >
              Confirm My Attendance →
            </button>
          </div>
        </div>
      </section>

      {/* Our Story Section - With Couple Photo */}
      <section className="story" id="story">
        <div className="story-grid">
          <div className="story-img"></div>
          <div className="story-body">
            <span className="eyebrow">Our story</span>
            <h2 className="s-title">
              A love <em>worth</em> saving the date for
            </h2>
            <div className="s-rule"></div>
            <p className="body-copy">
              Through the love and grace of God, our paths were aligned. 
              We met at church, and what began as a divine connection grew into a love story we could have never written ourselves.
            </p>
            <p className="body-copy">
              We are beyond grateful to celebrate this blessing surrounded by the people who have prayed for us, 
              loved us, and walked alongside us. Thank you for being part of our journey.
            </p>
          </div>
        </div>
      </section>

      {/* NEW: Venue Showcase Section */}
      <section className="venue" id="venue">
        <div className="venue-image"></div>
        <div className="venue-overlay">
          <div className="venue-content">
            <span className="eyebrow">Where we'll say "I do"</span>
            <h2 className="s-title venue-title">
              The Grand Tradition<br className="venue-break" /> <em>Estate and Gardens</em>
            </h2>
            <div className="s-rule"></div>
            <p className="venue-description">
              A garden oasis in Fallbrook California featuring waterfalls, 
              beautiful greenery and elegant architecture.
            </p>
            <div className="venue-details-box">
              <p className="venue-address">
                <strong>Location:</strong><br />
                220 Grand Tradition Way<br />
                Fallbrook, California 92028
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wedding Details */}
      <section className="details" id="details">
        <div className="section-wrap">
          <div className="details-head">
            <div className="details-left">
              <span className="eyebrow">Mark your calendar</span>
              <h2 className="s-title">Wedding <em>Details</em></h2>
              <div className="s-rule"></div>
            </div>
          </div>

          <div className="details-grid">
            <div className="d-item">
              <div className="d-num">01</div>
              <div className="d-label">The Date</div>
              <div className="d-val">November 7th, 2026</div>
              <div className="d-sub">Saturday</div>
              <div className="d-sub">Ceremony begins at 2:00 PM</div>
            </div>

            <div className="d-item">
              <div className="d-num">02</div>
              <div className="d-label">The Venue</div>
              <div className="d-val">Grand Tradition Estate and Gardens</div>
              <div className="d-sub">Fallbrook, California</div>
            </div>

            <div className="d-item">
              <div className="d-num">03</div>
              <div className="d-label">Dress Code</div>
              <div className="d-val">Black Tie Formal</div>
              <div className="d-sub">Garden ceremony on grass.</div>
              <div className="d-sub">We suggest comfortable dress shoes.</div>
            </div>

            <div className="d-item">
              <div className="d-num">04</div>
              <div className="d-label">Please Note</div>
              <div className="d-val">Adults Only Event</div>
              <div className="d-sub">We kindly request that this celebration remain adults only. We appreciate your understanding and look forward to an elegant evening together.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Schedule */}
      <section className="schedule" id="schedule">
        <div className="section-wrap">
          <div className="schedule-head">
            <span className="eyebrow">The day</span>
            <h2 className="s-title">The <em>Day's</em> Flow</h2>
            <div className="s-rule"></div>
            <p className="sched-note">
              All events take place on the estate. Please arrive promptly, the ceremony will begin at 2:00 PM.
            </p>
          </div>

          <ul className="sched-list">
            <li className="sched-row">
              <div className="sched-time">1:00 PM</div>
              <div className="sched-right">
                <div className="sched-event">Guests Arrive</div>
                <div className="sched-loc">Champagne greeting upon arrival between 1:15 PM to 1:45 PM.</div>
              </div>
            </li>
            <li className="sched-row">
              <div className="sched-time">2:00 PM</div>
              <div className="sched-right">
                <div className="sched-event">Ceremony</div>
                <div className="sched-loc">Please be seated at the Arbor Terrace Waterfall by 1:45 PM. <em className="sched-note-italic">Golf cart access to location upon request.</em></div>
              </div>
            </li>
            <li className="sched-row">
              <div className="sched-time">2:45 PM</div>
              <div className="sched-right">
                <div className="sched-event">Cocktail Hour</div>
                <div className="sched-loc">Join us at the Orchid Bar for cocktails and hors d'oeuvres. <em className="sched-note-italic">Located on site.</em></div>
              </div>
            </li>
            <li className="sched-row">
              <div className="sched-time">4:00 PM</div>
              <div className="sched-right">
                <div className="sched-event">Grand Reception Entrance</div>
                <div className="sched-loc">Newlywed welcome and grand entrance at Arbor Terrace.</div>
              </div>
            </li>
            <li className="sched-row">
              <div className="sched-time">4:30 PM</div>
              <div className="sched-right">
                <div className="sched-event">Dinner</div>
                <div className="sched-loc">A formal seated dinner will be served to celebrate the evening.</div>
              </div>
            </li>
            <li className="sched-row">
              <div className="sched-time">5:00 PM</div>
              <div className="sched-right">
                <div className="sched-event">First Dance & Celebrations</div>
                <div className="sched-loc">Our first dance is followed by an open dance floor for the evening.</div>
              </div>
            </li>
            <li className="sched-row">
              <div className="sched-time">9:00 PM</div>
              <div className="sched-right">
                <div className="sched-event">Farewell</div>
                <div className="sched-loc">We thank you for celebrating with us and wish you a safe journey home.</div>
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* Wedding Registry */}
      <section className="registry" id="registry">
        <div className="section-wrap">
          <div className="registry-header">
            <img src="/monogram-std.png" alt="" aria-hidden="true" className="section-monogram" />
            <span className="eyebrow">Our Registry</span>
            <h2 className="s-title">A gift toward our <em>next chapter</em></h2>
            <div className="s-rule center"></div>
            <p className="body-copy registry-intro">
              Your presence is the greatest gift of all. But if you'd like to help us start our happily-ever-after,
              a contribution to our honeymoon fund would mean the world to us.
            </p>
          </div>

          <div className="registry-cards">
            <a
              href={REGISTRY_LINKS.honeyfund}
              target="_blank"
              rel="noopener noreferrer"
              className="registry-card registry-card-honeyfund"
              data-testid="registry-honeyfund-card"
            >
              <div className="registry-card-glow" aria-hidden="true" />
              <div className="registry-card-inner">
                <div className="registry-icon" aria-hidden="true">✈</div>
                <div className="registry-card-eyebrow">Honeymoon Fund</div>
                <h3 className="registry-card-title">Contribute to our Honeymoon</h3>
                <p className="registry-card-body">
                  Help send us off on the adventure of a lifetime. Every contribution, big or small, is a memory we'll cherish forever.
                </p>
                <span className="registry-card-cta">Visit Honeyfund →</span>
              </div>
            </a>
          </div>

          <p className="registry-thanks">
            Thank you for the love. <em>Nathan &amp; Sandra</em>
          </p>
        </div>
      </section>

      {/* RSVP — hidden by default; revealed via Confirm CTA or Find My RSVP nav link */}
      {rsvpVisible && (
      <section className="rsvp" id="rsvp">
        <div className="section-wrap">
          <div className="rsvp-header">
            <img
              src="/monogram-std.png"
              alt=""
              aria-hidden="true"
              className="section-monogram"
            />
            <span className="eyebrow">Confirm Attendance</span>
            <h2 className="s-title">Kindly <em>confirm</em></h2>
            <div className="s-rule center"></div>
            <p className="body-copy rsvp-intro">
              Please confirm you're still able to celebrate with us. The ceremony begins promptly at
              <strong> 2:00 PM</strong>. Please be seated by <strong>1:45 PM</strong>. Questions? Reach out to Nathan or Sandra directly.
            </p>
            <p className="body-copy rsvp-intro-note">
              <em>Should there be any last minute changes to your party or an unforeseen inability to attend, kindly reach out to Nathan or Sandra directly at your earliest convenience.</em>
            </p>
          </div>

          <div className="rsvp-wrap">
            {/* STEP 1: Search */}
            {rsvpStep === 'search' && (
              <div className="rsvp-step rsvp-search-step" data-testid="rsvp-search-step">
                <div className="step-indicator">Step 1 of 2</div>
                <h3 className="step-title">Look Up Your Name</h3>
                <p className="step-sub">We can't wait to celebrate with you. Please enter your <strong>full name</strong> (first and last) so we can pull up your party.</p>

                <form onSubmit={handleSearch} className="search-form">
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Enter your full name (first and last)..."
                    value={searchQuery}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSearchQuery(val);
                      const tokens = val.trim().split(/\s+/).filter(t => t.length >= 2);
                      if (tokens.length >= 2) {
                        clearTimeout(window._searchTimer);
                        window._searchTimer = setTimeout(() => handleSearch(null, val), 250);
                      } else {
                        setSearchResults([]);
                      }
                    }}
                    data-testid="rsvp-search-input"
                    autoFocus
                  />
                  <button type="submit" className="search-btn" data-testid="rsvp-search-btn">
                    Search
                  </button>
                </form>

                {(() => {
                  const tokens = searchQuery.trim().split(/\s+/).filter(t => t.length >= 2);
                  if (tokens.length === 1) {
                    return <p className="search-hint">Please also enter your last name to find your reservation.</p>;
                  }
                  if (searching) return <p className="search-hint">Searching…</p>;
                  if (tokens.length >= 2 && searchResults.length === 0) {
                    return <p className="search-hint">No match found. Please check your spelling or contact us.</p>;
                  }
                  return null;
                })()}

                {searchResults.length > 0 && (
                  <div className="search-results" data-testid="rsvp-search-results">
                    {searchResults.map((g) => {
                      const matched = g.display_name || g.primary_guest;
                      return (
                        <button
                          key={`${g.id}-${matched}`}
                          className="result-card"
                          onClick={() => selectGuest(g)}
                          data-testid={`rsvp-result-${g.id}-${matched.replace(/\s+/g,'-').toLowerCase()}`}
                        >
                          <div className="result-name">{matched}</div>
                          <div className="result-arrow">→</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* STEP 2: Confirm Attendance + Questions */}
            {rsvpStep === 'confirm' && selectedGuest && (
              <div className="rsvp-step" data-testid="rsvp-confirm-step">
                <div className="step-indicator">Step 2 of 2</div>
                <h3 className="step-title" data-testid="rsvp-step-title">
                  {isUpdating ? 'Update Your Response' : 'Kindly confirm attendance'}
                </h3>
                <p className="step-sub" data-testid="rsvp-step-sub">
                  {isUpdating ? (
                    <>Hello, <strong>{selectedGuest.display_name || selectedGuest.primary_guest}</strong>. You may update your previous response below.</>
                  ) : (
                    <>Hello, <strong>{selectedGuest.display_name || selectedGuest.primary_guest}</strong>. The big day is almost here. Please let us know what services you'll be able to celebrate with us below.</>
                  )}
                </p>
                {isUpdating && (
                  <p className="update-notice" data-testid="update-notice">
                    ✓ We found your previous response. Feel free to update it.
                  </p>
                )}

                <div className="guest-list">
                  {guestList.map((person, idx) => {
                    const isPlaceholder = person.isPlusOne === true;
                    const nameMissing =
                      isPlaceholder && person.attending === true && !(person.name || '').trim();
                    return (
                      <div key={idx} className="guest-row guest-row-dual" data-testid={`guest-row-${idx}`}>
                        <div className="guest-name">
                          {isPlaceholder ? (
                            <div className="plus-one-input-wrap">
                              <label className="plus-one-label">Additional guest name</label>
                              <input
                                type="text"
                                className={`guest-name-input${nameMissing ? ' input-error' : ''}`}
                                placeholder="First and last name…"
                                value={person.name || ''}
                                onChange={(e) => {
                                  updateGuestName(idx, e.target.value);
                                  if (submitError) setSubmitError('');
                                }}
                                data-testid={`guest-name-input-${idx}`}
                                aria-invalid={nameMissing ? 'true' : 'false'}
                              />
                              {nameMissing && (
                                <span className="plus-one-error" data-testid={`guest-name-error-${idx}`}>
                                  Please enter your guest's name.
                                </span>
                              )}
                            </div>
                          ) : (
                            person.name
                          )}
                        </div>
                        <div className="guest-services">
                          <div className="service-block">
                            <div className="service-label">
                              <span className="svc-title">Ceremony · 2:00 PM</span>
                              <span className="svc-loc">Arbor Terrace Waterfall</span>
                            </div>
                            <div className="attend-toggle">
                              <button
                                type="button"
                                className={`toggle-btn small ${person.attending_ceremony ? 'active' : ''}`}
                                onClick={() => updateGuestService(idx, 'attending_ceremony', true)}
                                data-testid={`ceremony-yes-${idx}`}
                              >
                                Yes
                              </button>
                              <button
                                type="button"
                                className={`toggle-btn small ${person.attending_ceremony === false ? 'active no' : ''}`}
                                onClick={() => updateGuestService(idx, 'attending_ceremony', false)}
                                data-testid={`ceremony-no-${idx}`}
                              >
                                No
                              </button>
                            </div>
                          </div>
                          <div className="service-block">
                            <div className="service-label">
                              <span className="svc-title">Reception · 4:00 PM</span>
                              <span className="svc-loc">Arbor Terrace Grand Tent</span>
                            </div>
                            <div className="attend-toggle">
                              <button
                                type="button"
                                className={`toggle-btn small ${person.attending_reception ? 'active' : ''}`}
                                onClick={() => updateGuestService(idx, 'attending_reception', true)}
                                data-testid={`reception-yes-${idx}`}
                              >
                                Yes
                              </button>
                              <button
                                type="button"
                                className={`toggle-btn small ${person.attending_reception === false ? 'active no' : ''}`}
                                onClick={() => updateGuestService(idx, 'attending_reception', false)}
                                data-testid={`reception-no-${idx}`}
                              >
                                No
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <form
                  onSubmit={submitRsvp}
                  className="comment-form"
                  data-testid="rsvp-comment-form"
                >
                  <div className="f-group comment-block">
                    <label className="f-label" htmlFor="rsvp-questions">
                      Questions or a note for Nathan &amp; Sandra?
                    </label>
                    <textarea
                      id="rsvp-questions"
                      className="f-textarea"
                      rows="3"
                      placeholder="We'd love to hear from you — anything you'd like to ask or share about the day."
                      value={contactInfo.message}
                      onChange={(e) => setContactInfo({...contactInfo, message: e.target.value})}
                      data-testid="rsvp-message-input"
                    ></textarea>
                    <p className="f-help" data-testid="rsvp-message-help">
                      We'll receive your message and get back to you as soon as we can.
                    </p>
                  </div>

                  {submitError && <p className="error-msg">{submitError}</p>}

                  <div className="step-actions">
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={resetRsvp}
                      disabled={submitting}
                      data-testid="rsvp-back-btn"
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={submitting}
                      data-testid="rsvp-submit-btn"
                    >
                      {submitting ? 'Confirming…' : (isUpdating ? 'Update' : 'Confirm')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* STEP 4: Thank You */}
            {rsvpStep === 'thankyou' && (() => {
              const totalCount = guestList.length;
              const attendingCount = guestList.filter(g => g.attending).length;
              const allDeclined = totalCount > 0 && attendingCount === 0;
              const someDeclined = attendingCount > 0 && attendingCount < totalCount;

              return (
                <div className="rsvp-step thankyou-step" data-testid="rsvp-thankyou-step">
                  <div className="thankyou-icon">♥</div>
                  <h3 className="thankyou-title">
                    {allDeclined ? "With Gratitude" : "Thank You"}
                  </h3>
                  <div className="s-rule center"></div>

                  {allDeclined ? (
                    <>
                      <p className="thankyou-text" data-testid="thankyou-declined">
                        Thank you so much for letting us know. We'll truly miss having you with us,
                        but we deeply appreciate your kind response and the love behind it.
                      </p>
                      <p className="thankyou-text">
                        Wishing you all the best. We hope to see you soon.
                      </p>
                    </>
                  ) : someDeclined ? (
                    <>
                      <p className="thankyou-text" data-testid="thankyou-partial">
                        We've received your confirmation and your party updates. We're so glad some of you will be there to celebrate with us, and we'll miss those who can't make it.
                      </p>
                      <p className="thankyou-note" data-testid="thankyou-note">
                        Thank you for submitting. We look forward to celebrating with you on this special day.
                      </p>
                      <p className="thankyou-text">
                        A friendly reminder. The ceremony begins at <strong>2:00 PM</strong>. Please be seated by <strong>1:45 PM</strong>. Any questions, reach out to Nathan or Sandra directly.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="thankyou-text" data-testid="thankyou-attending">
                        We've received your confirmation and your party updates. We can't wait to celebrate with you on our big day.
                      </p>
                      <p className="thankyou-note" data-testid="thankyou-note">
                        Thank you for submitting. We look forward to celebrating with you on this special day.
                      </p>
                      <p className="thankyou-text">
                        A friendly reminder. The ceremony begins at <strong>2:00 PM</strong>. Please be seated by <strong>1:45 PM</strong>. Any questions, reach out to Nathan or Sandra directly.
                      </p>
                    </>
                  )}

                  <p className="thankyou-signature">
                    <em>With love,<br />Nathan &amp; Sandra</em>
                  </p>
                  <button className="btn-secondary thankyou-btn" onClick={resetRsvp} data-testid="rsvp-reset-btn">
                    {allDeclined ? "Close" : "Submit another response"}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      </section>
      )}

      {/* Footer with Monogram */}
      <footer>
        <div className="footer-inner">
          <div className="footer-left">
            <img src="/monogram-std.png" alt="N&S Monogram" className="footer-monogram" />
          </div>
          <div className="footer-center">
            <div className="footer-names">Nathan & Sandra</div>
            <div className="footer-date">November 7th, 2026</div>
            <div className="footer-location">The Grand Tradition Estate and Gardens</div>
            <div className="footer-location-sub">Fallbrook, California</div>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}

export default App;
